import axios, { AxiosHeaders, type AxiosRequestConfig } from "axios";

/**
 * HTTP contract: **`API_DOCUMENTATION.md` only** (no other API sources).
 *
 * Start with § **Admin application — API spec for AI agents / frontends**:
 * - Auth: **`POST /api/admin/admin_login`**, **`POST /api/admin/register_admin`** (§ `/api/admin`) — no JWT on those calls.
 * - After login: **every** admin-authenticated request must send `Authorization: Bearer <jwt>` and header **`type: admin`** (exactly).
 * - Slots: § **`/api/slots`** (legacy/materialized + platform defaults) and § **`/api/turf-slots`** (weekly / override / available).
 *
 * Base URL: `REACT_APP_API_BASE_URL` or `http://127.0.0.1:3100/api` (trailing slashes normalized).
 */
//const configuredBaseUrl = "https://api.easyturf.in/api";
const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:3100/api";
const normalizedBaseUrl = configuredBaseUrl.replace(/\/+$/, "");
const baseUrl = `${normalizedBaseUrl}/`;

const apiClient = axios.create({
    baseURL: baseUrl,
    timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        const headers = AxiosHeaders.from(config.headers ?? {});
        headers.set("Authorization", `Bearer ${token}`);
        headers.set("type", "admin");
        config.headers = headers;
    }
    return config;
});

/** Extra axios config (params, etc.). Admin JWT + `type: admin` are applied by the interceptor when a token exists. */
const authConfig = (config: AxiosRequestConfig = {}): AxiosRequestConfig => config;

export const uploadFile = (data: any) => {
    return apiClient.post("upload", data);
}

// export const uploadFile = (data: any) => {
//     return axios.post(baseUrl + 'banner_upload/upload_banner', data);
// }

export const addBanner = (data: any) => {
    return apiClient.post("banner/add_banner", data, authConfig());
}

export const getAllBanner = () => {
    return apiClient.get("banner/get_banner", authConfig());
}

export const deleteBanner = (data: any) => {
    return apiClient.post("banner/delete_banner", data, authConfig());
}

export const uploadImage = (data: any) => {
    return apiClient.post("upload", data);
}

export const addPartner = (data: any) => {
    return apiClient.post("partner/add_partner", data, authConfig());
}

export const addPartnerBannerImage = (data: any) => {
    return apiClient.post("partner/add_banner_image", data, authConfig());
}

export const getAllPartner = (limit: any, page: any) => {
    return apiClient.get("partner/get_partners_admin", authConfig({
        params: { limit, page },
    }));
}
// export const getAllPartner = (limit: any, page: any, latitude: any, longitude: any) => {
//     return axios.get(baseUrl + `partner/get_partners?limit=${limit}&page=${page}&latitude=${latitude}&longitude=${longitude}`);
// }

export const deletePartner = (data: any) => {
    return apiClient.post("partner/delete_partner", data, authConfig());
}

export const getPartnerById = (data: any) => {
    return apiClient.post("partner/get_partner_by_id", data, authConfig());
}

export const updatePartner = (data: any) => {
    return apiClient.post("partner/update_partner", data, authConfig());
}

export const addCity = (data: any) => {
    return apiClient.post("city/add_city", data, authConfig());
}

export const getAllCities = () => {
    return apiClient.get("city/get_all_cities", authConfig());
}

export const getAllPartners = () => {
    return apiClient.get("partner/get_all_partner", authConfig());
}

export const deleteCities = (data: any) => {
    return apiClient.post("city/delete_city", data, authConfig());
}

export const addBox = (data: any) => {
    return apiClient.post("boxes/add_box", data, authConfig());
}

export const updateBox = (data: any) => {
    return apiClient.post("boxes/update_box", data, authConfig());
}

export const deleteBoxes = (data: any) => {
    return apiClient.post("boxes/delete_box", data, authConfig());
}

export const getBoxbyId = (id: any) => {
    return apiClient.get("boxes/get_all_box_by_partner_id", authConfig({
        params: { partnerId: id },
    }));
}

export const addSlots = (data: any) => {
    return apiClient.post("slots/add_slot", data, authConfig());
}

/** All materialized slot rows for a box (legacy list). */
export const getSlotbyId = (id: any) => {
    return apiClient.get("slots/get_slots_by_boxid", authConfig({
        params: { box_id: id },
    }));
}

/**
 * Slots for one box + date: legacy `slots` rows, or synthetic rows from turf availability
 * (`id: null`, `resolved_from: "turf_availability"`). API_DOCUMENTATION.md § get_slot_by_date.
 */
export const getSlotByDate = (data: { box_id: number; date: string }) => {
    return apiClient.post("slots/get_slot_by_date", data, authConfig());
}

/** `GET /api/turf-slots/available` — admin app still sends JWT + `type: admin` per § Admin application. */
export const getTurfSlotsAvailable = (turfId: number, date: string) => {
    return apiClient.get("turf-slots/available", authConfig({
        params: { turf_id: turfId, date },
    }));
}

/** `GET /api/turf-slots/weekly` — effective weekly rows or platform defaults. */
export const getTurfSlotsWeekly = (turfId: number, dayOfWeek: number) => {
    return apiClient.get("turf-slots/weekly", authConfig({
        params: { turf_id: turfId, day_of_week: dayOfWeek },
    }));
}

/** `GET /api/turf-slots/override` — date override for a turf (query: turf_id, date). */
export const getTurfSlotsOverride = (turfId: number, date: string) => {
    return apiClient.get("turf-slots/override", authConfig({
        params: { turf_id: turfId, date },
    }));
}

/** `POST /api/turf-slots/weekly` — replace all slots for one weekday (admin: any turf_id). */
export const postTurfSlotsWeekly = (data: any) => {
    return apiClient.post("turf-slots/weekly", data, authConfig());
}

/**
 * `POST /api/turf-slots/weekly_schedule` — one transaction: bulk set Sun–Sat or selected `days[]`.
 * Auth same as `/weekly` (JWT + type: partner with matching turf_id, or type: admin).
 */
export const postTurfSlotsWeeklySchedule = (data: any) => {
    return apiClient.post("turf-slots/weekly_schedule", data, authConfig());
}

/** `POST /api/turf-slots/override` — replace one date or set is_closed (admin: any turf_id). */
export const postTurfSlotsOverride = (data: any) => {
    return apiClient.post("turf-slots/override", data, authConfig());
}

export const updateSlot = (data: any) => {
    return apiClient.post("slots/update_slots", data, authConfig());
}

export const deleteSlots = (data: any) => {
    return apiClient.post("slots/delete_slot", data, authConfig());
}

// § /api/slots — Platform default templates (admin).
export const getPlatformDefaultSlots = () => {
    return apiClient.get("slots/platform_default_slots", authConfig());
}

export const getAllDefaultSlots = () => {
    return apiClient.get("slots/get_all_default_slots", authConfig());
}

export const addPlatformDefaultSlot = (data: any) => {
    return apiClient.post("slots/add_platform_default_slot", data, authConfig());
}

/** Legacy: optional partnerId seeds partner boxes 15 days; omit partnerId = platform (admin). */
export const addDailySlot = (data: any) => {
    return apiClient.post("slots/add_daily_slot", data, authConfig());
}

export const updatePlatformDefaultSlot = (data: any) => {
    return apiClient.post("slots/update_platform_default_slot", data, authConfig());
}

export const updateDefaultSlots = (data: any) => {
    return apiClient.post("slots/update_default_slots", data, authConfig());
}

export const deletePlatformDefaultSlot = (data: any) => {
    return apiClient.post("slots/delete_platform_default_slot", data, authConfig());
}

export const deleteDefaultSlot = (data: any) => {
    return apiClient.post("slots/delete_default_slot", data, authConfig());
}

export const addAmenities = (data: any) => {
    return apiClient.post("amenities/add_amenities", data, authConfig());
}

export const addSports = (data: any) => {
    return apiClient.post("sports/add_sport", data, authConfig());
}

export const deleteAmenity = (data: any) => {
    return apiClient.post("amenities/delete_amenity", data, authConfig());
}

export const getAmenities = () => {
    return apiClient.get("amenities/get_all_amenities", authConfig());
}

export const getSport = () => {
    return apiClient.get("sports/get_all_sports", authConfig());
}

export const deleteSport = (data: any) => {
    return apiClient.post("sports/delete_sport", data, authConfig());
}

export const getTotalCounts = () => {
    return apiClient.get("dashboard/get_all_counts", authConfig());
}

export const getAllUsers = () => {
    return apiClient.get("user/get_all_users", authConfig());
}

export const getuserById = (data: any) => {
    return apiClient.post("user/get_user_by_id", data, authConfig());
}

// --- User fingerprint / device / session analytics (admin) -------------------

/**
 * `GET /api/user-devices/by_user/:userId` — Auth: Bearer + `type: admin`.
 * Returns { devices: [], sessions: [] } — every device/install we've seen for
 * this user and the most recent 50 app sessions.
 */
export const getUserDevices = (userId: number | string) => {
    return apiClient.get(`user-devices/by_user/${encodeURIComponent(String(userId))}`, authConfig());
}

/**
 * `GET /api/user-devices/stats/:userId` — Auth: Bearer + `type: admin`.
 * Returns derived behaviour insights (preferred sport, box, time bucket,
 * weekday, total bookings, avg booking amount).
 */
export const getUserStats = (userId: number | string) => {
    return apiClient.get(`user-devices/stats/${encodeURIComponent(String(userId))}`, authConfig());
}

export const getAllBooking = (limit: any, page: any) => {
    return apiClient.get("booking/get_all_bookings", authConfig({
        params: { limit, page },
    }));
}

/** `POST /api/admin/register_admin` — no JWT. */
export const registerAdmin = (data: any) => {
    return apiClient.post("admin/register_admin", data);
}

/** `POST /api/admin/admin_login` — no JWT. */
export const loginAdmin = (data: any) => {
    return apiClient.post("admin/admin_login", data);
}

export const getPlayerid = (data: any) => {
    return apiClient.post("store-player-id", data);
}

export const sendNotification = (data: any) => {
    return apiClient.post("notification/add_notification", data, authConfig());
}

export const deleteNotification = (data: any) => {
    return apiClient.post("notification/delete_notification", data, authConfig());
}

export const getNotification = (limit: any, page: any) => {
    return apiClient.get("notification/get_notification", authConfig({
        params: { limit, page },
    }));
}

export const addAdvertisements = (data: any) => {
    return apiClient.post("advertisementBanner/add_advertisement_banner", data, authConfig());
}

export const getAdvertisements = () => {
    return apiClient.get("advertisementBanner/get_all_advertisements_banner", authConfig());
}

export const deleteAdvertisements = (data: any) => {
    return apiClient.post("advertisementBanner/delete_advertisement_banner", data, authConfig());
}

export const addSettingsData = (data: any) => {
    return apiClient.post("settings/add_data", data, authConfig());
}

export const addTournament = (data: any) => {
    return apiClient.post("tournaments/add_tournament", data, authConfig());
}

export const getTournaments = (limit: any, page: any) => {
    return apiClient.get("tournaments/get_tournament", authConfig({
        params: { limit, page },
    }));
}

export const getTournamentById = (data: any) => {
    return apiClient.post("tournaments/get_tournament_by_id", data, authConfig());
}

export const updateTournament = (data: any) => {
    return apiClient.post("tournaments/update_tournament", data, authConfig());
}

export const deleteTournamentDetails = (data: any) => {
    return apiClient.post("tournaments/delete_tournament", data, authConfig());
}

export const addOffer = (data: any) => {
    return apiClient.post("offer/add_offer", data, authConfig());
}

export const getOffer = () => {
    return apiClient.get("offer/get_offer", authConfig());
}

export const getOfferById = (data: any) => {
    return apiClient.post("offer/get_offer_by_id", data, authConfig());
}

export const updateOffer = (data: any) => {
    return apiClient.post("offer/update_offer", data, authConfig());
}

export const deleteOffer = (data: any) => {
    return apiClient.post("offer/delete_offer", data, authConfig());
}

export const getSettingsData = () => {
    return apiClient.get("settings/get_data", authConfig());
}

export const getAllBookingByPartner = (data: any, limit: any, page: any) => {
    return apiClient.get("booking/get_booking_by_partner", authConfig({
        params: { partner_id: data, limit, page },
    }));
}

export const getAllBookingForAnalytics = (data: any) => {
    return apiClient.get("booking/get_all_bookings_for_analytics", authConfig({
        params: { partner_id: data },
    }));
}

export const getAdevrtisement = (limit: any, page: any) => {
    return apiClient.get("advertisement/get_advertisement", authConfig({
        params: { limit, page },
    }));
}

export const getAdvertisementById = (data: any) => {
    return apiClient.post("advertisement/get_advertisement_by_id", data, authConfig());
}

export const addAdvertisement = (data: any) => {
    return apiClient.post("advertisement/add_advertisement", data, authConfig());
}

export const updateAdvertisement = (data: any) => {
    return apiClient.post("advertisement/update_advertisement", data, authConfig());
}

export const deleteAdvertisementById = (data: any) => {
    return apiClient.post("advertisement/delete_advertisement", data, authConfig());
}
export const getBannerByPartner = (id: any) => {
    return apiClient.get("banner/get_partner_banner", authConfig({
        params: { id: id },
    }));
}

export const getAllPartnerCoupons = () => {
    return apiClient.get("partner-coupon/get_all", authConfig());
}

export const deletePartnerCouponAdmin = (id: number | string) => {
    return apiClient.post("partner-coupon/delete", { id }, authConfig());
}